import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { useIntl } from 'react-intl';
import { styled } from 'stitches.config';
import { toCurrency } from '@/utils/numberFormatter';
import Typography from '@/components/sushi/Typography';
import { GraphTooltip } from './GraphTooltip';
import { GraphData } from './types';
import { SimulatorLegend } from '../simulators/SimulatorLegend';

interface GraphProps {
  graphData: GraphData;
  nbYears: number;
  displayPlaceholder?: boolean;
}

const placeholderData = [
  {
    initialPatrimony: 100000,
    investment: 200000,
    netValue: 248234,
    year: 1,
  },
  {
    initialPatrimony: 100000,
    investment: 300000,
    netValue: 472795,
    year: 2,
  },
  {
    initialPatrimony: 100000,
    investment: 400000,
    netValue: 818152,
    year: 3,
  },
  {
    initialPatrimony: 100000,
    investment: 500000,
    netValue: 1354668,
    year: 4,
  },
  {
    initialPatrimony: 100000,
    investment: 600000,
    netValue: 2193699,
    year: 5,
  },
];

export const Graph = ({ graphData, nbYears, displayPlaceholder }: GraphProps) => {
  const { formatMessage } = useIntl();

  return (
    <Container>
      <GraphContainer height={380} width="100%">
        <AreaChart data={displayPlaceholder ? placeholderData : graphData} margin={{ top: 15 }}>
          <CartesianGrid vertical={false} strokeDasharray="2 2" stroke="#2E2F33" />

          <YAxis
            tickLine={false}
            tick={{ fontSize: 10, transform: 'translate(56, -9)' }}
            axisLine={false}
            tickMargin={0}
            tickFormatter={(props) => toCurrency(props, 'EUR', 0, { notation: 'compact' })}
          />
          {!displayPlaceholder && (
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              content={({ payload }) => {
                if (!payload[0]) {
                  return null;
                }
                const { year, netValue, initialPatrimony, investment } = payload[0].payload;

                return (
                  <GraphTooltip
                    year={year}
                    netValue={netValue}
                    initialPatrimony={initialPatrimony}
                    investment={investment}
                  />
                );
              }}
            />
          )}

          <Area
            connectNulls
            type="monotone"
            dataKey="netValue"
            stackId="3"
            strokeWidth={2}
            fillOpacity={0.32}
            activeDot={{ r: 4, stroke: '0' }}
            stroke={displayPlaceholder ? '#333333' : '#F49352'}
            fill={displayPlaceholder ? '#333333' : '#F49352'}
          />
          <Area
            connectNulls
            type="monotone"
            dataKey="investment"
            stackId="2"
            strokeWidth={2}
            fillOpacity={displayPlaceholder ? 0.12 : 0.7}
            activeDot={{ r: 4, stroke: '0' }}
            stroke={displayPlaceholder ? '#333333' : '#6F50E5'}
            fill={displayPlaceholder ? '#333333' : '#241A49'}
          />
          <Area
            connectNulls
            type="monotone"
            dataKey="initialPatrimony"
            stackId="1"
            strokeWidth={2}
            fillOpacity={displayPlaceholder ? 0.02 : 0.7}
            activeDot={{ r: 4, stroke: '0' }}
            stroke={displayPlaceholder ? '#333333' : '#D6475D'}
            fill={displayPlaceholder ? '#333333' : '#44171E'}
          />
        </AreaChart>
      </GraphContainer>
      <AxisValues>
        <Typography variant="bodyXS" color="webappTertiary">
          {formatMessage({ id: 'wealth_simulator.chart.axis_min' })}
        </Typography>
        <Typography variant="bodyXS" color="webappTertiary">{`${nbYears} ${formatMessage({
          id: 'wealth_simulator.chart.axis_max',
        })}`}</Typography>
      </AxisValues>
      <SimulatorLegend items={[
        {
            label: formatMessage({ id: 'wealth_simulator.chart.legend_curve_1' }),
            color: '#D6475D',
        },
        {
            label: formatMessage({ id: 'wealth_simulator.chart.legend_curve_2' }),
            color: '#6F50E5',
        },
        {
            label: formatMessage({ id: 'wealth_simulator.chart.legend_curve_3' }),
            color: '#F49352',
        }
      ]} />
    </Container>
  );
};

const Container = styled('div', {
  marginBottom: '48px',
  '@sm': {
    marginBottom: '32px',
  },
});

const GraphContainer = styled(ResponsiveContainer, {
  // By default rechart has a margin left that we can't remove if we want to display the labels of the axis
  // This is a trick to display it the way we want anyway (ie with no margin - which are SVG calculated)
  width: 'calc(100% + 60px) !important',
  marginLeft: '-60px',
});

const AxisValues = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 30px 16px',
});
